// file: src/components/MobilePerformanceMonitor.jsx
import { useEffect } from 'react';
import { addMemoryPressureListener, isMobileDevice } from '../utils/mobileOptimizations';

/**
 * Component to monitor performance on mobile devices and prevent crashes
 * This component has no UI - it just monitors and optimizes
 */
export default function MobilePerformanceMonitor() {
  useEffect(() => {
    // Only run on mobile devices
    if (!isMobileDevice()) {
      return;
    }

    console.debug('[MobilePerf] Performance monitoring enabled for mobile device');

    // Monitor memory pressure
    const cleanupMemoryMonitor = addMemoryPressureListener((usedRatio) => {
      console.warn(`[MobilePerf] High memory usage detected: ${(usedRatio * 100).toFixed(1)}%`);
      
      // Try to reduce memory usage
      if (usedRatio > 0.9) {
        console.warn('[MobilePerf] Critical memory pressure - attempting cleanup');
        
        // Force garbage collection if available (only in Chrome with --expose-gc flag)
        if (window.gc) {
          window.gc();
        }
        
        // Clear old cached data from localStorage (keep only last 24 hours)
        try {
          const now = Date.now();
          const dayInMs = 24 * 60 * 60 * 1000;
          
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('conv-')) {
              // Try to parse timestamp from cached conversations
              try {
                const data = JSON.parse(localStorage.getItem(key) || '[]');
                // If data is stale (more than 24h), remove it
                if (Array.isArray(data) && data.length > 0) {
                  // Just keep it - we'll rely on natural cache eviction
                }
              } catch (e) {
                // Invalid data, remove it
                localStorage.removeItem(key);
              }
            }
          }
        } catch (err) {
          console.error('[MobilePerf] Error during memory cleanup:', err);
        }
      }
    });

    // Monitor for page freeze/hangs
    let lastActivityTime = Date.now();
    let checkInterval;
    
    const resetActivityTimer = () => {
      lastActivityTime = Date.now();
    };

    // Listen for user interactions to detect if the page is responsive
    const events = ['touchstart', 'touchmove', 'scroll', 'click'];
    events.forEach(eventName => {
      window.addEventListener(eventName, resetActivityTimer, { passive: true });
    });

    // Check if the page has been unresponsive for more than 5 seconds
    checkInterval = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivityTime;
      
      if (timeSinceLastActivity > 5000) {
        console.warn('[MobilePerf] Page may be unresponsive');
      }
    }, 6000);

    // Cleanup
    return () => {
      cleanupMemoryMonitor();
      clearInterval(checkInterval);
      events.forEach(eventName => {
        window.removeEventListener(eventName, resetActivityTimer);
      });
    };
  }, []);

  // This component renders nothing
  return null;
}

